import { useRef, useState } from "react";
import { Download, Pause, Play } from "lucide-react";
import SCRIPT from "../../lib/demoScript.json";

const CUTS = { full: "Full tour", social: "30-second cut" };

const Frame = ({ s }) => {
  if (s.kind === "intro" || s.kind === "cta") {
    return (
      <div className="w-full aspect-video bg-canvas grid place-items-center text-center px-6" data-testid={`demo-card-${s.kind}`}>
        <div className="rise">
          <img src="/logo.webp" alt="AEGIS Guard" className={`mx-auto object-contain drop-shadow-[0_0_40px_rgba(56,189,248,0.4)] ${s.kind === "intro" ? "w-48 md:w-72" : "w-24 md:w-32"}`} />
          {s.kind === "cta" && <>
            <p className="text-2xl md:text-4xl font-extrabold mt-4">Ready to see your actual savings?</p>
            <div className="flex flex-wrap justify-center gap-3 mt-5">
              <a href="#roi" className="px-5 py-2.5 bg-brand text-black font-mono text-xs font-bold" data-testid="demo-cta-calc">CALCULATE YOUR SAVINGS</a>
              <a href="#chat" className="px-5 py-2.5 border border-white/25 font-mono text-xs" data-testid="demo-cta-chat">CHAT WITH MARI</a>
            </div>
            <p className="font-mono text-[11px] text-white/45 mt-4">15% of recovered funds · $0 if you lose · no monthly minimum</p>
          </>}
        </div>
      </div>
    );
  }
  return (
    <div className="relative w-full aspect-video overflow-hidden bg-canvas">
      <img src={`/demo/${s.img}.jpg`} alt={`AEGIS ${s.id}`} key={s.id} className={`w-full h-full object-cover object-top ${s.kind === "quote" ? "kenburns" : "demo-fade"}`} data-testid="demo-frame" />
      {s.kind === "quote" && <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />}
      {s.hl && (
        <div key={`${s.id}-hl`} className="absolute border-2 border-brand demo-hl" style={{ left: `${s.hl.x}%`, top: `${s.hl.y}%`, width: `${s.hl.w}%`, height: `${s.hl.h}%` }} data-testid="demo-highlight">
          <span className="absolute -top-7 left-0 px-2 py-1 bg-brand text-black font-mono text-[10px] font-bold whitespace-nowrap">{s.hl.label}</span>
        </div>
      )}
    </div>
  );
};

export const DemoPlayer = () => {
  const [cut, setCut] = useState("full");
  const slides = SCRIPT[cut];
  const [i, setI] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const gen = useRef(0);
  const audio = useRef(null);
  const halt = useRef(null);
  const done = i >= slides.length;

  const run = async (from, list = slides, c = cut) => {
    const id = ++gen.current; setPlaying(true);
    for (let k = from; k < list.length; k++) {
      if (id !== gen.current) return;
      setI(k);
      const a = new Audio(`/demo/${c}-${String(k + 1).padStart(2, "0")}.mp3`);
      audio.current = a;
      await new Promise((res) => { halt.current = res; const later = () => setTimeout(res, 5000); a.onended = res; a.onerror = later; a.play().catch(later); });
    }
    if (id === gen.current) { setPlaying(false); setI(list.length); }
  };
  const stop = () => { gen.current++; audio.current?.pause(); halt.current?.(); setPlaying(false); };
  const toggle = () => (playing ? stop() : done ? run(0) : run(Math.max(i, 0)));
  const switchCut = (c) => { stop(); setCut(c); setI(-1); };
  const cur = slides[Math.min(Math.max(i, 0), slides.length - 1)];
  const caption = i < 0 ? `Watch the narrated ${cut === "full" ? "tour" : "30-second cut"} of the real platform.` : done ? "That's AEGIS Guard. Calculate your savings below, or chat with MARI." : cur.caption || cur.text;

  return (
    <div data-testid="demo-player">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex border border-white/15">
          {Object.entries(CUTS).map(([k, l]) => (
            <button key={k} onClick={() => switchCut(k)} className={`px-4 py-2 font-mono text-[11px] tracking-wider transition-colors ${cut === k ? "bg-white text-black" : "text-white/60 hover:text-white"}`} data-testid={`demo-cut-${k}`}>{l.toUpperCase()}</button>
          ))}
        </div>
        <div className="flex gap-4 font-mono text-[11px]">
          <a href="/demo/aegis-demo-full.mp4" download className="flex items-center gap-1.5 text-white/60 hover:text-brand transition-colors" data-testid="download-mp4-full"><Download className="w-3.5 h-3.5" /> FULL MP4</a>
          <a href="/demo/aegis-demo-social.mp4" download className="flex items-center gap-1.5 text-white/60 hover:text-brand transition-colors" data-testid="download-mp4-social"><Download className="w-3.5 h-3.5" /> 30s SOCIAL MP4</a>
        </div>
      </div>
      <div className="relative border border-white/10 bg-canvas overflow-hidden">
        <Frame s={i < 0 ? slides[0] : cur} />
        <div className="relative md:absolute inset-x-0 bottom-0 bg-canvas md:bg-transparent md:bg-gradient-to-t md:from-black md:via-black/85 md:to-transparent p-5 md:pt-16 pointer-events-none">
          <p className={`text-white max-w-3xl ${cur?.kind === "quote" && i >= 0 && !done ? "text-lg md:text-2xl font-bold italic" : "text-sm md:text-base"}`} data-testid="demo-caption">{caption}</p>
          <div className="flex gap-1 mt-3">{slides.map((_, k) => <span key={k} className={`h-1 flex-1 ${k <= i ? "bg-brand" : "bg-white/15"}`} />)}</div>
        </div>
        <button onClick={toggle} data-testid="demo-play-btn" className="absolute top-4 right-4 flex items-center gap-2 px-4 py-2 bg-brand text-black font-mono text-xs font-bold tracking-wider hover:shadow-[0_0_28px_var(--brand)] transition-shadow">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} {playing ? "PAUSE" : i < 0 ? "PLAY NARRATED DEMO" : done ? "REPLAY" : "RESUME"}
        </button>
      </div>
    </div>
  );
};
